import { Resources } from "@/types/subject-schema";
import katex from "katex";
import {
  getLegacyDisplayMaxWidth,
  getTierDisplayMaxWidth,
  ImageContext,
  nestedImageStyle,
  SizeTier,
} from "./imageSizing";
import {
  ContentToken,
  KeywordHtmlResult,
  KeywordMap,
  KeywordRenderContext,
  KeywordToken,
} from "./types";

const MISSING_CONTENT_HTML =
  '<span class="katex-error-message">Missing content. Please report this to feedback@tibertutor.com.</span>';

const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

/** Precompiled regex + map for matching syllabus terms to their definition content. */
type DefinitionMatcher = {
  regex: RegExp;
  keywordMap: KeywordMap;
};

/** Regions where definition tooltips must not be applied (nested tag depth). */
type TagDepth = { table: number; heading: number; blockQuote: number; paragraph: number; element: number };

/**
 * Build a matcher from the definition keyword map (term → tokenized definition).
 * Longest terms win; optional trailing "s" matches plurals (e.g. "electrons" → electron).
 */
function buildDefinitionMatcher(
  keywordMap: KeywordMap
): DefinitionMatcher | null {
  const keys = Object.keys(keywordMap);
  if (keys.length === 0) return null;

  const keywordPattern = keys
    .sort((a, b) => b.length - a.length)
    .map((k) => `${k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}s?`)
    .join("|");

  const regex = new RegExp(`(?<!-)\\b(${keywordPattern})\\b(?![-'])`, "gi");
  return { regex, keywordMap };
}

/** Turn matched surface text into a keyword token, or null if not in the map. */
function keywordTokenFromMatch(
  captured: string,
  keywordMap: KeywordMap
): Extract<ContentToken, { type: "keyword" }> | null {
  let lookupKey = captured.toLowerCase();
  if (!(lookupKey in keywordMap) && lookupKey.endsWith("s")) {
    lookupKey = lookupKey.slice(0, -1);
  }
  if (!(lookupKey in keywordMap)) return null;
  return {
    type: "keyword",
    text: captured,
    definition: keywordMap[lookupKey],
  };
}

/** Track entering/leaving tables, headings, blockquotes, and generic element nesting. */
function updateTagDepthForTag(tag: string, depth: TagDepth): void {
  const m = tag.match(/^<\s*(\/?)\s*([a-zA-Z0-9]+)/i);
  if (!m) return;
  const isClosing = m[1] === "/";
  const name = m[2].toLowerCase();
  const delta = isClosing ? -1 : 1;

  if (name === "table") {
    depth.table += delta;
  } else if (/^h[1-6]$/.test(name)) {
    depth.heading += delta;
  } else if (name === "blockquote") {
    depth.blockQuote += delta;
  } else if (name === "p") {
    depth.paragraph += delta;
  }

  if (!tag.endsWith("/>") && !VOID_ELEMENTS.has(name)) {
    depth.element += isClosing ? -1 : 1;
    if (depth.element < 0) depth.element = 0;
  }
}

/**
 * Scan forward through plain HTML text until the next tag (`<`) or LaTeX (`$`).
 * Escaped `\$` is consumed as part of the segment (literal dollar sign).
 */
function findHtmlTextSegmentEnd(raw: string, start: number): number {
  let end = start;
  while (end < raw.length) {
    if (raw[end] === "<") return end;
    if (raw[end] === "\\" && end + 1 < raw.length && raw[end + 1] === "$") {
      end += 2;
      continue;
    }
    if (raw[end] === "$") return end;
    end++;
  }
  return end;
}

/**
 * Process a stretch of HTML body text: either append as-is, or split into
 * `html` + `keyword` tokens where syllabus terms match the definition map.
 */
function tokenizeHtmlTextForKeywords(
  text: string,
  shouldHighlightKeywords: boolean,
  definitionMatcher: DefinitionMatcher | null,
  flush: () => void,
  tokens: ContentToken[],
  htmlBuf: { value: string }
): void {
  const normalized = text.replace(/\\\$/g, "$");
  if (!normalized) return;

  if (!shouldHighlightKeywords || !definitionMatcher) {
    htmlBuf.value += normalized;
    return;
  }

  definitionMatcher.regex.lastIndex = 0;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let foundKeyword = false;

  while ((match = definitionMatcher.regex.exec(normalized)) !== null) {
    const captured = match[1];
    if (!captured) continue;

    const kwToken = keywordTokenFromMatch(
      captured,
      definitionMatcher.keywordMap
    );
    if (!kwToken) continue;

    foundKeyword = true;
    const kwStart = match.index;
    const kwEnd = kwStart + captured.length;

    if (kwStart > lastIndex) {
      flush();
      tokens.push({
        type: "html",
        value: normalized.substring(lastIndex, kwStart),
      });
    } else {
      flush();
    }
    tokens.push(kwToken);
    lastIndex = kwEnd;
  }

  if (foundKeyword) {
    if (lastIndex < normalized.length) {
      htmlBuf.value += normalized.substring(lastIndex);
    }
  } else {
    htmlBuf.value += normalized;
  }
}

/**
 * Parse CMS HTML into tokens: plain HTML, LaTeX, images, and (when keywordMap
 * is provided) keyword spans wired to tokenized definitions for tooltips.
 */
export function tokenizeContent(
  raw: string,
  resource: Resources,
  keywordMap?: KeywordMap
): ContentToken[] {
  if (!raw) {
    return [{ type: "html", value: MISSING_CONTENT_HTML }];
  }

  const definitionMatcher =
    keywordMap && Object.keys(keywordMap).length > 0
      ? buildDefinitionMatcher(keywordMap)
      : null;

  const tokens: ContentToken[] = [];
  let i = 0;
  const htmlBuf = { value: "" };
  // Nested <table> / <h1>–<h6> — terms inside these are left as plain HTML.
  const tagDepth: TagDepth = { table: 0, heading: 0, blockQuote: 0, paragraph: 0, element: 0 };

  function flush() {
    if (htmlBuf.value) {
      tokens.push({ type: "html", value: htmlBuf.value });
      htmlBuf.value = "";
    }
  }

  while (i < raw.length) {
    // Escaped dollar (\$) — emit literal $
    if (raw[i] === "\\" && i + 1 < raw.length && raw[i + 1] === "$") {
      htmlBuf.value += "$";
      i += 2;
      continue;
    }

    // Display LaTeX: $$...$$
    if (raw[i] === "$" && i + 1 < raw.length && raw[i + 1] === "$") {
      const closeIdx = findClosingDollar(raw, i + 2, true);
      if (closeIdx === -1) {
        htmlBuf.value += "$$";
        i += 2;
        continue;
      }
      flush();
      const body = raw.substring(i + 2, closeIdx);
      tokens.push({
        type: "latex",
        displayMode: true,
        html: renderLatex(body),
      });
      i = closeIdx + 2;
      continue;
    }

    // Inline LaTeX: $...$
    if (raw[i] === "$") {
      const closeIdx = findClosingDollar(raw, i + 1, false);
      if (closeIdx === -1) {
        htmlBuf.value += "$";
        i += 1;
        continue;
      }
      flush();
      const body = raw.substring(i + 1, closeIdx);
      tokens.push({
        type: "latex",
        displayMode: false,
        html: renderLatex(body),
      });
      i = closeIdx + 1;
      continue;
    }

    // <img> tag
    if (
      raw[i] === "<" &&
      i + 4 < raw.length &&
      raw.substring(i, i + 4).toLowerCase() === "<img"
    ) {
      const endBracket = raw.indexOf(">", i);
      if (endBracket !== -1) {
        const imgTag = raw.substring(i, endBracket + 1);
        const imgToken = parseImgTag(imgTag, resource);
        if (imgToken) {
          flush();
          if (tagDepth.element <= 1 && tagDepth.paragraph <= 1) {
            tokens.push(imgToken);
          } else {
            tokens.push({ ...imgToken, nested: true });
          }
          i = endBracket + 1;
          continue;
        }
      }
    }

    // HTML tag — copy verbatim (never match keywords inside attributes).
    if (raw[i] === "<") {
      const endBracket = raw.indexOf(">", i);
      if (endBracket !== -1) {
        const tag = raw.substring(i, endBracket + 1);
        updateTagDepthForTag(tag, tagDepth);
        htmlBuf.value += tag;
        i = endBracket + 1;
        continue;
      }
    }

    const segmentEnd = findHtmlTextSegmentEnd(raw, i);
    if (segmentEnd === i) {
      htmlBuf.value += raw[i];
      i += 1;
      continue;
    }

    const shouldHighlightKeywords =
      tagDepth.table === 0 &&
      tagDepth.heading === 0 &&
      tagDepth.blockQuote === 0;
    tokenizeHtmlTextForKeywords(
      raw.substring(i, segmentEnd),
      shouldHighlightKeywords,
      definitionMatcher,
      flush,
      tokens,
      htmlBuf
    );
    i = segmentEnd;
  }

  flush();
  return tokens;
}

export function tokensToHtmlWithKeywords(
  tokens: ContentToken[],
  termClass: string,
  imageContext?: ImageContext
): KeywordHtmlResult {
  const keywords: KeywordToken[] = [];
  const html = tokens
    .map((t) =>
      tokenToHtmlChunk(t, { termClass, keywords }, imageContext)
    )
    .join("");
  return { html, keywords };
}

export function tokensToHtml(
  tokens: ContentToken[],
  imageContext?: ImageContext
): string {
  return tokens
    .map((t) => tokenToHtmlChunk(t, undefined, imageContext))
    .join("");
}

function nestedImageToHtml(
  t: Extract<ContentToken, { type: "image" }>,
  imageContext?: ImageContext
): string {
  const style = nestedImageStyle(t.width, imageContext, t.displayMaxWidth);
  const src = escapeHtmlAttr(t.src);
  const alt = escapeHtmlAttr(t.alt);
  return `<img src="${src}" alt="${alt}" width="${t.width}" height="${t.height}" style="${style}" />`;
}

function escapeHtmlAttr(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function tokenToHtmlChunk(
  t: ContentToken,
  kw: KeywordRenderContext | undefined,
  imageContext?: ImageContext
): string {
  switch (t.type) {
    case "html":
      return t.value;
    case "latex":
      return t.displayMode
        ? `<p class="centeredKatex" style="width:100%;text-align:center;">${t.html}</p>`
        : t.html;
    case "image":
      return t.nested ? nestedImageToHtml(t, imageContext) : "";
    case "keyword":
      if (kw) {
        const idx = kw.keywords.length;
        kw.keywords.push(t);
        return `<span data-kw-idx="${idx}" class="${
          kw.termClass
        }">${t.text.replaceAll(" ", "\u00A0")}</span>`;
      }
      return t.text;
  }
}

function findClosingDollar(
  raw: string,
  start: number,
  isDouble: boolean
): number {
  let j = start;
  while (j < raw.length) {
    if (raw[j] === "\\" && j + 1 < raw.length && raw[j + 1] === "$") {
      j += 2;
      continue;
    }
    if (isDouble) {
      if (raw[j] === "$" && j + 1 < raw.length && raw[j + 1] === "$") return j;
    } else {
      if (raw[j] === "$") return j;
    }
    j++;
  }
  return -1;
}

function renderLatex(body: string): string {
  try {
    // first, restore the HTML-sotred entities strapi replaces itself that Latex needs to render properly
    body = body.replaceAll("amp;","&")
    return katex.renderToString(body, { strict: true });
  } catch (e: any) {
    return `<span class="katex-error-wrapper">Equation failed to display. Please report this to feedback@tibertutor.com.<span class="katex-error-message">'${e.rawMessage}'</span></span>`;
  }
}

function parseImgTag(
  imgTag: string,
  resource: Resources
): Extract<ContentToken, { type: "image" }> | null {
  const srcMatch = imgTag.match(/src="([^"]+)"/);
  const altMatch = imgTag.match(/alt="([^"]+)"/);
  if (!srcMatch) return null;

  const src = srcMatch[1];
  const rawAlt = altMatch ? altMatch[1] : "";

  const dimMatch = src.match(/_([0-9]+)x([0-9]+)\./);
  if (!dimMatch) return null;

  let width = parseInt(dimMatch[1], 10);
  let height = parseInt(dimMatch[2], 10);

  const tierMatch = src.match(/_(Small|Medium|Large)_[0-9]+x[0-9]+\./);
  let displayMaxWidth = tierMatch
    ? getTierDisplayMaxWidth(tierMatch[1] as SizeTier)
    : getLegacyDisplayMaxWidth(width);

  const styleWidthMatch = imgTag.match(/width:([\d.]+)%/);
  if (styleWidthMatch) {
    const pct = parseFloat(styleWidthMatch[1]) / 100;
    width = Math.round(width * pct);
    height = Math.round(height * pct);
    displayMaxWidth = Math.round(displayMaxWidth * pct);
  }

  const alt = createAltFromTitle(rawAlt, resource) ?? rawAlt;
  return { type: "image", src, alt, width, height, displayMaxWidth };
}

export function createAltFromTitle(
  altTag: string,
  resource: Resources
): string | undefined {
  switch (resource) {
    case "questions": {
      const m = altTag.match(
        /[^0-9]+([0-9]+)\.([0-9]+)\.([0-9-]+)[.]?([aq]?)([0-9]*)_/
      );
      if (!m) return undefined;
      const [, topic, subTopic, relQ, imgType, imgNum] = m;
      return `Topic ${topic} subTopic ${subTopic} question ${relQ} ${
        imgType === "a" ? "answer" : "question"
      } image ${imgNum !== "" ? imgNum : "1"}`;
    }
    case "notes": {
      // we extract Bio/Chem/Phys from filename starting with capital letter and then lowercase letter
      const m = altTag.match(
        /([A-Z][a-z]{1,3})[a-zA-Z]+\.?([0-9]+)\.([0-9]+)\.?(?:note\.)?[a-zA-Z]*([0-9]+)_/
      );
      if (!m) return undefined;
      return `${m[1]} Topic ${m[2]} subTopic ${m[3]} notes image ${m[4]}`;
    }
    case "flashcards": {
      // we extract B/C/P from filename starting with capital letter
      const m = altTag.match(
        /([A-Z])([0-9]+)\.([0-9]+)\.(?:flash\.)?[a-zA-Z]*([0-9]+)_/
      );
      if (!m) return undefined;
      return `${m[1]} Topic ${m[2]} subTopic ${m[3]} flashcard image ${m[4]}`;
    }
  }
  return undefined;
}
